import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { authService, eventPackageService, charactersService } from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import type { EventPackageInfoDto, EventPackageRedemptionDto } from '../types';
import '../styles/PromoCodeSection.css';

const PromoCodeSection: React.FC = () => {
    const { t, locale } = useLanguage();
    const dateLocale = locale === 'pt-BR' ? 'pt-BR' : 'en-US';
    const isAuthenticated = authService.isAuthenticated();

    const [code, setCode] = useState('');
    const [info, setInfo] = useState<EventPackageInfoDto | null>(null);
    const [checking, setChecking] = useState(false);
    const [redeemed, setRedeemed] = useState<EventPackageRedemptionDto[]>([]);
    const [characters, setCharacters] = useState<any[]>([]);
    const [selectedChar, setSelectedChar] = useState<number>(0);
    const [selectedClass, setSelectedClass] = useState<string>('');
    const [redeeming, setRedeeming] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const redeemingRef = useRef(false);

    const loadCharacters = async () => {
        try {
            const data = await charactersService.getMyCharacters();
            const list = Array.isArray(data) ? data : [];
            setCharacters(list);
            if (list.length) {
                setSelectedChar(Number(list[0]?.dbKey ?? list[0]?.DBKey ?? 0));
            }
        } catch (e) {
            console.error('Erro ao carregar personagens na promo:', e);
            setCharacters([]);
        }
    };

    useEffect(() => {
        if (!isAuthenticated) return;
        loadCharacters();
        eventPackageService.getStatus()
            .then((data) => setRedeemed(data.redeemed ?? []))
            .catch(() => {});
    }, [isAuthenticated]);

    const handleValidate = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        const trimmed = code.trim();
        if (!trimmed) return;
        setChecking(true);
        setMessage(null);
        try {
            const data = await eventPackageService.getInfo(trimmed);
            setInfo(data);
            if (data.valid) {
                const classNames = Array.from(
                    new Set((data.items ?? []).map((i) => i.className).filter((c): c is string => !!c))
                );
                setSelectedClass(classNames.length ? classNames[0] : '');
            }
        } catch (err) {
            setInfo({ valid: false, error: 'Could not validate the code.' });
        } finally {
            setChecking(false);
        }
    };

    const handleRedeem = async () => {
        if (!info?.valid || selectedChar <= 0 || redeemingRef.current) return;
        if (!window.confirm(
            info.package
                ? `Você deseja resgatar ${selectedClass ? `a caixa da classe ${selectedClass}` : `o pacote "${info.package.name}"`} para o personagem escolhido?\n\nApenas 1 caixa por classe. Esta ação é definitiva.`
                : 'Você deseja resgatar este pacote?\n\nApenas 1 caixa por classe. Esta ação é definitiva.'
        )) return;

        redeemingRef.current = true;
        setRedeeming(true);
        setMessage(null);
        try {
            const res = await eventPackageService.redeem(code, selectedChar, selectedClass || undefined);
            if (res.success && res.payload) {
                setMessage({ type: 'success', text: res.message });
                setRedeemed(res.payload.redeemed ?? []);
            } else {
                setMessage({ type: 'error', text: res.message });
            }
        } catch (e: any) {
            setMessage({ type: 'error', text: e?.response?.data?.message || t('promo.error') });
        } finally {
            setRedeeming(false);
            redeemingRef.current = false;
        }
    };

    const classItems = (info?.items ?? []).filter((i) => i.className);
    const hasClassSelection = classItems.length > 0;
    const redeemedClasses = info?.valid && info.package
        ? new Set(
              redeemed
                  .filter((r) => r.packageId === info.package!.id && r.className)
                  .map((r) => r.className as string)
          )
        : new Set<string>();
    const shownItems = info?.valid
        ? (info.items ?? []).filter((i) => !i.className || !selectedClass || i.className === selectedClass)
        : [];
    const allDone = info?.valid && hasClassSelection && classItems.every((i) => redeemedClasses.has(i.className ?? ''));
    const globalDone = Boolean(info?.valid && info.package && !hasClassSelection &&
        redeemed.some((r) => r.packageId === info.package!.id));
    const done = Boolean(info?.valid && info.package) && (allDone || globalDone);
    const lastForPackage = (info?.package?.id ? redeemed.find((r) => r.packageId === info.package!.id) : undefined) ?? undefined;

    if (isAuthenticated && done && info?.package) {
        return (
            <section className="promo-section">
                <div className="container">
                    <div className="promo-card promo-card--done">
                        <div className="promo-done-icon">
                            <i className="ri-checkbox-circle-line"></i>
                        </div>
                        <h2>{hasClassSelection ? t('promo.allDoneTitle') : t('promo.alreadyTitle')}</h2>
                        <p>{t('promo.alreadyDesc')}</p>
                        {lastForPackage && (
                            <div className="promo-done-detail">
                                <span className="promo-done-pkg">{info.package.name}</span>
                                {lastForPackage.characterName && (
                                    <span className="promo-done-char">{lastForPackage.characterName}</span>
                                )}
                                {lastForPackage.redeemedAt && (
                                    <span className="promo-done-date">
                                        {t('promo.alreadyStatus')}:{' '}
                                        {new Date(lastForPackage.redeemedAt).toLocaleDateString(dateLocale, {
                                            day: 'numeric', month: 'long', year: 'numeric',
                                        })}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="promo-section">
            <div className="container">
                <div className="promo-card">
                    <div className="promo-header">
                        <span className="promo-icon"><i className="ri-gift-2-line"></i></span>
                        <div>
                            <h2>{t('promo.title')}</h2>
                            <p>{t('promo.desc')}</p>
                        </div>
                    </div>

                    {message && (
                        <div className={`promo-message promo-message-${message.type}`}>
                            {message.text}
                        </div>
                    )}

                    <form className="promo-code-form" onSubmit={handleValidate}>
                        <input
                            type="text"
                            className="promo-input"
                            placeholder={t('promo.codePlaceholder')}
                            value={code}
                            onChange={(e) => {
                                setCode(e.target.value);
                                setInfo(null);
                            }}
                        />
                        <button type="submit" className="btn btn-primary" disabled={checking || !code.trim()}>
                            {checking ? t('promo.validating') : t('promo.validate')}
                        </button>
                    </form>

                    {info?.valid && info.package && (
                        <div className="promo-package">
                            <div className="promo-package-head">
                                <h3>{info.package.name}</h3>
                                {info.package.description && <p>{info.package.description}</p>}
                            </div>

                            {hasClassSelection && (
                                <>
                                    <div className="promo-choose-label">
                                        {t('promo.chooseClass')} {redeemedClasses.size > 0 && `(${t('promo.redeemedClasses')}: ${redeemedClasses.size})`}
                                    </div>
                                    <div className="promo-classes">
                                        {classItems.map((item) => {
                                            const isDone = redeemedClasses.has(item.className ?? '');
                                            return (
                                                <button
                                                    key={item.className ?? item.recId}
                                                    type="button"
                                                    disabled={isDone}
                                                    className={`promo-class-card ${selectedClass === item.className ? 'selected' : ''} ${isDone ? 'done' : ''}`}
                                                    onClick={() => setSelectedClass(item.className ?? '')}
                                                >
                                                    <span className="promo-class-icon">
                                                        <i className={isDone ? 'ri-check-double-line' : 'ri-user-star-line'}></i>
                                                    </span>
                                                    <span className="promo-class-name">{item.className}</span>
                                                    {isDone && <span className="promo-class-done-tag">{t('promo.redeemed')}</span>}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </>
                            )}

                            <div className="promo-items">
                                {shownItems.map((item, idx) => (
                                    <div key={`${item.recId}-${idx}`} className="promo-item">
                                        <i className="ri-coupon-line"></i>
                                        <div>
                                            <span className="promo-item-name">{item.itemName || item.className || 'Item'}</span>
                                            {item.className && (
                                                <span className="promo-item-meta">{item.className}</span>
                                            )}
                                        </div>
                                        <span className="promo-item-amount">x{item.amount ?? 1}</span>
                                    </div>
                                ))}
                            </div>

                            {!isAuthenticated ? (
                                <div className="promo-login-note">
                                    <i className="ri-lock-line"></i>
                                    <span>{t('promo.loginNeeded')}</span>
                                    <Link to="/login" className="btn btn-secondary">{t('promo.login')}</Link>
                                </div>
                            ) : (
                                <div className="promo-redeem">
                                    <div className="promo-choose-label">{t('promo.chooseChar')}</div>
                                    {characters.length === 0 ? (
                                        <div className="promo-no-chars">
                                            <i className="ri-user-line"></i>
                                            <p>{t('promo.noChars')}</p>
                                        </div>
                                    ) : (
                                        <div className="promo-redeem-bar">
                                            <select
                                                className="promo-select"
                                                value={selectedChar}
                                                onChange={(e) => setSelectedChar(Number(e.target.value))}
                                            >
                                                {characters.map((c: any) => (
                                                    <option key={Number(c.dbKey ?? c.DBKey ?? 0)} value={Number(c.dbKey ?? c.DBKey ?? 0)}>
                                                        {c.name ?? c.Name ?? ''} (Lv.{c.level ?? c.Level ?? 0} · {c.className ?? c.ClassName ?? ''})
                                                    </option>
                                                ))}
                                            </select>
                                            <button
                                                type="button"
                                                className="btn btn-primary"
                                                disabled={redeeming || selectedChar <= 0}
                                                onClick={handleRedeem}
                                            >
                                                {redeeming ? t('promo.redeeming') : t('promo.redeem')}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {info && !info.valid && (
                        <div className="promo-message promo-message-error">
                            {info.error || t('promo.codeInvalid')}
                        </div>
                    )}

                </div>
            </div>
        </section>
    );
};

export default PromoCodeSection;